// Zuwad (WhatsApp style, per user): the signed-in user's archived and pinned
// conversations, and the time each chat last mentioned the inbox's own line.
// Kept apart from the conversation objects because conversation.updated and
// message.created payloads built by the workers carry neither, so a live update
// never clears them.
import types from '../mutation-types';
import ZuwadConversationMarksAPI from '../../api/zuwadConversationMarks';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';
import { hasUnreadLineMention } from './conversations/helpers/zuwadMarks';

const SUMMARY_REFRESH_MS = 2000;

export const state = {
  // display id => true
  archived: {},
  // display id => pinned_at (epoch seconds)
  pinned: {},
  // display id => zuwad_line_mention_at (epoch seconds)
  mentions: {},
  loadedAccountId: null,
};

export const getters = {
  isArchived: $state => id => Boolean($state.archived[id]),
  getPinnedAt: $state => id => $state.pinned[id] || 0,
  hasLineMention: $state => conversation =>
    hasUnreadLineMention(
      conversation,
      Math.max(
        $state.mentions[conversation.id] || 0,
        conversation.zuwad_line_mention_at || 0
      )
    ),
};

// The Archived row (meta.archived) for the list on screen; the archived list and
// advanced filters have no Archived row.
const fetchSummary = async (dispatch, rootState) => {
  const { conversationFilters, appliedFilters } = rootState.conversations;
  if (conversationFilters.archived || appliedFilters.length) return;
  try {
    const {
      data: { meta },
    } = await ConversationApi.meta(conversationFilters);
    dispatch('conversationStats/set', meta, { root: true });
  } catch (error) {
    // The next list fetch or live event refreshes it.
  }
};

const debouncedFetchSummary = debounce(fetchSummary, SUMMARY_REFRESH_MS);

// Replaces the marks and loads the chats that moved into the list on screen
// (unarchived while the main list shows, archived while the archived list shows);
// chats that moved out drop from the list through the conversation getters.
const setMarks = ({ commit, dispatch, state: $state, rootState }, data) => {
  const before = $state.archived;
  commit(types.SET_ZUWAD_CONVERSATION_MARKS, data);
  const { allConversations, conversationFilters } = rootState.conversations;
  const archivedList = Boolean(conversationFilters.archived);
  const moved = archivedList
    ? data.archived.filter(id => !before[id])
    : Object.keys(before)
        .map(Number)
        .filter(id => !$state.archived[id]);
  moved
    .filter(id => !allConversations.some(c => c.id === id))
    .forEach(id => dispatch('getConversation', id, { root: true }));
};

export const actions = {
  fetch: async context => {
    const { data } = await ZuwadConversationMarksAPI.get();
    setMarks(context, data);
    context.commit(
      types.SET_ZUWAD_CONVERSATION_MARKS_LOADED,
      context.rootGetters.getCurrentAccountId
    );
  },
  ensureLoaded: ({ state: $state, dispatch, rootGetters }) => {
    if ($state.loadedAccountId === rootGetters.getCurrentAccountId) return;
    dispatch('fetch').catch(() => {});
  },
  // payload: { conversationIds, archived } or { conversationIds, pinned }.
  // Rejects with the API error (422 pin_limit, archived; 404).
  update: async (context, payload) => {
    const { data } = await ZuwadConversationMarksAPI.patch(payload);
    setMarks(context, data);
    await fetchSummary(context.dispatch, context.rootState);
  },
  // zuwad.conversation_marks.updated: the user's full state from any session.
  applyMarksEvent: (context, data) => {
    setMarks(context, data);
    context.dispatch('refreshSummary');
  },
  // zuwad.conversation.mention: { id, zuwad_line_mention_at }.
  applyMentionEvent: ({ commit, dispatch, getters: $getters }, data) => {
    commit(types.RECORD_ZUWAD_LINE_MENTIONS, [data]);
    if ($getters.isArchived(data.id)) dispatch('refreshSummary');
  },
  recordMentions: ({ commit }, conversations) => {
    commit(types.RECORD_ZUWAD_LINE_MENTIONS, conversations);
  },
  refreshSummary: ({ dispatch, rootState }) => {
    debouncedFetchSummary(dispatch, rootState);
  },
};

export const mutations = {
  [types.SET_ZUWAD_CONVERSATION_MARKS]($state, { archived, pinned }) {
    $state.archived = Object.fromEntries(archived.map(id => [id, true]));
    $state.pinned = Object.fromEntries(
      pinned.map(({ id, pinned_at: pinnedAt }) => [id, pinnedAt])
    );
  },
  [types.SET_ZUWAD_CONVERSATION_MARKS_LOADED]($state, accountId) {
    $state.loadedAccountId = accountId;
  },
  // Keeps the latest time per chat; entries without a mention are skipped.
  [types.RECORD_ZUWAD_LINE_MENTIONS]($state, conversations) {
    conversations.forEach(({ id, zuwad_line_mention_at: mentionAt }) => {
      if (mentionAt > ($state.mentions[id] || 0)) {
        $state.mentions[id] = mentionAt;
      }
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
